import React, { useEffect, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/react-hooks";
import { useElements, useStripe } from "@stripe/react-stripe-js";
import { Modal, Toggle } from "components/common";
import cardBack from "assets/issuing/card_back.png";
import cardFront from "assets/issuing/card_front.png";
import { ISSUING_CARD_STATUS } from '../constants';
import { GET_CARD_EPHEMERAL_KEY_SECRET_QUERY, GET_ISSUING_CARD_STATUS_QUERY, UPDATE_ISSUING_CARD_STATUS, GET_ISSUING_CARD_DETAILS_QUERY } from "../gql";

const CardDetailsModal = ({ elementsLoaded, isOpen, closeModal }) => {
  const [loading, setLoading] = useState(false);
  const { data: { card } = {}, loading: cardLoading, refetch } = useQuery(GET_ISSUING_CARD_STATUS_QUERY);
  const [updateCardStatus] = useMutation(UPDATE_ISSUING_CARD_STATUS);
  const [errorMessage, setErrorMessage] = useState('');
  const { cardholder } = card || {};
  const { name, billing_address } = cardholder || {};
  const { street, city, state, zip } = billing_address || {};

  const updateStatus = async (status) => {
    const toStatus = status === ISSUING_CARD_STATUS.ACTIVE ? "Unlock" : "Lock";
    let errorOccured = false;
    if (!status) { return; }

    setLoading(true);
    updateCardStatus({ variables: { status } })
      .then(() => {
        setErrorMessage('');
      })
      .catch(() => {
        console.log('error occured');
        errorOccured = true;
        setErrorMessage(`Unable to ${toStatus} card. Please try again or reach out to customer support`);
      })
      .finally(() => {
        if (errorOccured) {
          setLoading(false);
        } else {
          refetch().then(() => { setLoading(false); });
        }
      });
  };

  return (
    <Modal
      isOpen={isOpen}
      close={closeModal}
      options={{ showCloseButton: false }}
    >
      <div
        className="flex flex-col justify-center items-center bg-white rounded-xl px-4 py-10"
        style={{ maxWidth: 400 }}
      >
        <div className="relative">
          <img src={cardBack} alt="card back" className="rounded-md" />
          {!elementsLoaded && (
            <div
              className="loader loaderPurple absolute bottom-0 left-0 m-auto right-0 top-0"
              style={{ height: "80px", width: "80px" }}
            />
          )}
          {/* TODO: Cardholder Name */}
          <div className="w-full absolute bottom-0 mb-6 space-y-1 xs:space-y-3 ml-2 xs:ml-6 text-xl">
            {elementsLoaded && <div className="uppercase">{name}</div>}
            <div id="card-number" className="w-full h-6" />
            <div className="flex" style={{ maxWidth: "300px", height: 25 }}>
              <div className="w-full flex items-end">
                {elementsLoaded && <div className="mr-2 text-sm">EXP:</div>}
                <div
                  id="card-expiry"
                  className="w-full h-6"
                  style={{ maxHeight: 40 }}
                />
              </div>
              <div className="w-full flex items-end">
                {elementsLoaded && <div className="mr-2 text-sm">CVV:</div>}
                <div
                  id="card-cvc"
                  className="w-full h-6"
                  style={{ maxHeight: 40 }}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 self-start">
          {elementsLoaded && (
            <>
              <div className="font-bold">Billing Address:</div>
              <div>
                <span>{street}</span>
              </div>
              <div>
                <span>
                  {city}, {state}, {zip}
                </span>
              </div>
            </>
          )}
        </div>
        <div className="flex mt-4">
          {cardLoading || loading ? (
            <div
              className="loader loaderPurple h-auto mx-auto block"
              style={{ width: 32, height: 32 }}
            />
          ) : (
            <>
              <span className="px-2">
                Card Status:{" "}
                <span
                  className={`font-bold ${
                    card.status === ISSUING_CARD_STATUS.ACTIVE
                      ? "text-green-500"
                      : "text-red-500"
                  }`}
                >
                  {card.status === ISSUING_CARD_STATUS.ACTIVE
                    ? "Active"
                    : "Locked"}
                </span>
              </span>
              <Toggle
                active={card.status === "active"}
                className="sliderPurple"
                onChange={() => {
                  const newStatus =
                    card.status === ISSUING_CARD_STATUS.ACTIVE
                      ? ISSUING_CARD_STATUS.INACTIVE
                      : ISSUING_CARD_STATUS.ACTIVE;
                  updateStatus(newStatus);
                }}
              />
            </>
          )}
        </div>
        {errorMessage && !loading && (
          <span className="py-2 text-sm mx-8 text-red-500">{errorMessage}</span>
        )}
        <button
          type="button"
          className="mt-8 rounded-full px-20 py-2 bg-boost-light-purple text-white font-semibold z-50"
          onClick={closeModal}
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

const IssuingApprovedCard = ({ cardId }) => {
  const stripe = useStripe();
  const elements = useElements();
  const client = useApolloClient();
  const [elementsLoaded, setElementsLoaded] = useState(false);
  const [showCardDetailsModal, setShowCardDetailsModal] = useState(false);

  useEffect(() => {
    const mountCardDetails = async () => {
      if (!stripe || !elements || !showCardDetailsModal || !cardId) {
        return;
      }

      const nonceResult = await stripe.createEphemeralKeyNonce({
        issuingCard: cardId,
        options: {
          apiVersion: "2023-10-16",
        },
      });

      const { nonce } = nonceResult;

      const response = await client.query({
        query: GET_CARD_EPHEMERAL_KEY_SECRET_QUERY,
        variables: { nonce },
      });

      if (!response?.data) {
        throw new Error("Something went wrong");
      }

      const ephemeralKeySecret = response.data.getCardEphemeralKeySecret.secret;
      const cardNumberStyle = {
        base: {
          color: "black",
          fontWeight: 700, // text-bold
          fontSize: "1.5rem",
          letterSpacing: "0.1em",
          backgroundClip: "text",
          textFillColor: "transparent",
        },
      };

      elements
        .create("issuingCardNumberDisplay", {
          issuingCard: cardId,
          ephemeralKeySecret,
          nonce,
          style: cardNumberStyle,
        })
        .mount("#card-number");

      elements
        .create("issuingCardExpiryDisplay", {
          issuingCard: cardId,
          ephemeralKeySecret,
          nonce,
          style: cardNumberStyle,
        })
        .mount("#card-expiry");

      elements
        .create("issuingCardCvcDisplay", {
          issuingCard: cardId,
          ephemeralKeySecret,
          nonce,
          style: cardNumberStyle,
        })
        .mount("#card-cvc");

      // TODO: there is a slight delay from loader disappears and CC details show
      setElementsLoaded(true);
    };

    mountCardDetails();
  }, [stripe, cardId, elements, showCardDetailsModal]);

  return (
    <div
      className="w-full px-4 py-2 shadow flex flex-col items-center"
      style={{ minWidth: 240 }}
    >
      <p className="font-bold mr-1 text-xl">Your Card</p>
      <div className="flex flex-col h-full justify-center items-center">
        <div className="w-full">
          <button
            className="ml-2 underline focus:outline-none"
            type="button"
            onClick={() => setShowCardDetailsModal(true)}
          >
            <img
              src={cardFront}
              alt="card front"
              className="rounded-md mt-4"
              style={{ maxWidth: 200 }}
            />
          </button>
        </div>
      </div>
      {showCardDetailsModal && (
        <CardDetailsModal
          isOpen={showCardDetailsModal}
          elementsLoaded={elementsLoaded}
          closeModal={() => {
            setShowCardDetailsModal(false);
            setElementsLoaded(false);
          }}
        />
      )}
    </div>
  );
};

export default IssuingApprovedCard;
